import React, { useState, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { calculatePlanetaryPositions, calculateAscendant, calculateHouses, getHouseFromCusps } from '../utils/ephemeris';
import { getNatalActivationManifestations, generateDailySummary, generateWeeklySummary } from '../utils/aspectInterpretations';
import type { DailySummary, WeeklySummary, LifeEventAlert, WeeklyLifeEventAlert } from '../utils/aspectInterpretations';
import { civilToUTC } from '../utils/natalDate';

interface NatalToTransitCalendarProps {
  natalDate: Date;
  /** Exact birth UTC instant (stored profiles). When provided it is used
   *  directly, bypassing the wall-clock reconstruction from natalDate. */
  natalDateUTC?: Date;
  natalLatitude: number;
  natalLongitude: number;
  natalTimezone: string;
  firstHouseReference: string;
  manualFirstHouseSign?: string;
  cityName?: string;
  houseSystem: string;
  showWeekNavigation?: boolean;
  currentWeekOnly?: boolean;
  allowedYears?: number[];
  monthlyAccess?: boolean;
  // Permission-based access control
  nttWeekly?: boolean;
  nttMonthly?: boolean;
  nttAnnual?: boolean;
  // Subscription dates (ISO strings from DynamoDB)
  nttWeeklyStartDate?: string;
  nttMonthlyStartDate?: string;
  nttAnnualStartDate?: string;
  nttAnnualEndDate?: string; // 'YYYY-MM-DD': an annual calendar bought for a year opens until the end of that year
}

// Planet symbols
const PLANET_SYMBOLS: Record<string, string> = {
  'Sun': '☉',
  'Moon': '☽',
  'Mercury': '☿',
  'Venus': '♀',
  'Mars': '♂',
  'Jupiter': '♃',
  'Saturn': '♄',
  'Uranus': '♅',
  'Neptune': '♆',
  'Pluto': '♇',
  'North Node': '☊',
  'South Node': '☋'
};

// Planet colors for natal planet highlighting
const PLANET_COLORS: Record<string, string> = {
  'Sun': '#FFD700',        // Gold
  'Moon': '#C0C0C0',       // Silver
  'Mercury': '#87CEEB',    // Sky Blue
  'Venus': '#FF69B4',      // Hot Pink
  'Mars': '#DC143C',       // Crimson
  'Jupiter': '#FFA500',    // Light Orange
  'Saturn': '#8B4513',     // Saddle Brown
  'Uranus': '#00CED1',     // Dark Turquoise
  'Neptune': '#9370DB',    // Medium Purple
  'Pluto': '#BA55D3',      // Medium Orchid (Purple-Magenta)
  'North Node': '#32CD32', // Lime Green
  'South Node': '#228B22'  // Forest Green
};

const ASPECT_TYPES = [
  { name: 'Conjunction', angle: 0, color: '#9370DB', symbol: '☌' },
  { name: 'Opposition', angle: 180, color: '#FFA500', symbol: '☍' },
  { name: 'Trine', angle: 120, color: '#4169E1', symbol: '△' },
  { name: 'Square', angle: 90, color: '#DC143C', symbol: '□' },
  { name: 'Sextile', angle: 60, color: '#32CD32', symbol: '⚹' }
];

const ZODIAC_SIGNS = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
];

// Planet speed order (fastest to slowest)
const PLANET_SPEED_ORDER: Record<string, number> = {
  'Moon': 1,
  'Sun': 2,
  'Mercury': 3,
  'Venus': 4,
  'Mars': 5,
  'Jupiter': 6,
  'Saturn': 7,
  'Uranus': 8,
  'Neptune': 9,
  'Pluto': 10,
  'North Node': 11,
  'South Node': 12
};

// Get orb for transit-to-transit aspects (tighter orbs, matching Personal Transit Calendar)
const getTransitToTransitOrb = (planet1: string, planet2: string, aspectName: string): number => {
  const innerPlanets = ['Sun', 'Mercury', 'Venus', 'Mars']; // Moon excluded
  const nodes = ['North Node', 'South Node'];

  const isInner1 = innerPlanets.includes(planet1);
  const isInner2 = innerPlanets.includes(planet2);
  const isNode1 = nodes.includes(planet1);
  const isNode2 = nodes.includes(planet2);

  // Nodes
  if (isNode1 || isNode2) {
    if (aspectName === 'Conjunction' || aspectName === 'Opposition') return 5;
    if (aspectName === 'Trine' || aspectName === 'Square') return 4;
    return 3;
  }

  // Jupiter-Saturn specific (generational aspects)
  if ((planet1 === 'Jupiter' && planet2 === 'Saturn') || (planet1 === 'Saturn' && planet2 === 'Jupiter')) {
    if (aspectName === 'Conjunction' || aspectName === 'Opposition') return 6;
    if (aspectName === 'Trine' || aspectName === 'Square') return 5;
    return 4;
  }

  // Outer to outer planets (slow-moving)
  if (!isInner1 && !isInner2) {
    if (aspectName === 'Conjunction' || aspectName === 'Opposition') return 5;
    if (aspectName === 'Trine' || aspectName === 'Square') return 4;
    return 3;
  }

  // Mixed: Outer to inner planets
  if ((isInner1 && !isInner2) || (!isInner1 && isInner2)) {
    if (aspectName === 'Conjunction' || aspectName === 'Opposition') return 4;
    if (aspectName === 'Trine' || aspectName === 'Square') return 3.5;
    return 2.5;
  }

  // Inner to inner planets (fast-moving, tightest orbs)
  if (aspectName === 'Conjunction' || aspectName === 'Opposition') return 3;
  if (aspectName === 'Trine' || aspectName === 'Square') return 2.5;
  return 2;
};

// Get orb for natal-to-transit-transit aspects (when natal planet aspects the midpoint)
const getNatalToMidpointOrb = (natalPlanet: string, aspectName: string): number => {
  const innerPlanets = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars'];
  const nodes = ['North Node', 'South Node'];

  const isInner = innerPlanets.includes(natalPlanet);
  const isNode = nodes.includes(natalPlanet);

  // Moon natal aspects (tightest)
  if (natalPlanet === 'Moon') {
    if (aspectName === 'Conjunction' || aspectName === 'Opposition') return 2;
    if (aspectName === 'Trine' || aspectName === 'Square') return 1.5;
    return 1;
  }

  // Sun natal aspects
  if (natalPlanet === 'Sun') {
    if (aspectName === 'Conjunction' || aspectName === 'Opposition') return 3;
    if (aspectName === 'Trine' || aspectName === 'Square') return 2.5;
    return 2;
  }

  // Nodes
  if (isNode) {
    if (aspectName === 'Conjunction' || aspectName === 'Opposition') return 3;
    if (aspectName === 'Trine' || aspectName === 'Square') return 2.5;
    return 2;
  }

  // Inner planets (Mercury, Venus, Mars)
  if (isInner) {
    if (aspectName === 'Conjunction' || aspectName === 'Opposition') return 2.5;
    if (aspectName === 'Trine' || aspectName === 'Square') return 2;
    return 1.5;
  }

  // Outer planets (Jupiter, Saturn, Uranus, Neptune, Pluto)
  if (aspectName === 'Conjunction' || aspectName === 'Opposition') return 3;
  if (aspectName === 'Trine' || aspectName === 'Square') return 2.5;
  return 2;
};

// Calculate angular difference
const getAngularDifference = (angle1: number, angle2: number): number => {
  let diff = Math.abs(angle1 - angle2);
  if (diff > 180) diff = 360 - diff;
  return diff;
};

// House lookup now goes through getHouseFromCusps() from utils/ephemeris, which
// handles the unequal cusps that Placidus produces (the old helper here assumed
// 30° per house and silently turned Placidus into Equal House).

// Format house ordinal
const getOrdinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

// Helper function to check if natal planet aspects the midpoint of transit-to-transit aspect
const checkNatalToTransitAspect = (
  natalPlanetName: string,
  natalLongitude: number,
  transit1Longitude: number,
  transit2Longitude: number
): { aspect: any; orb: number } | null => {
  // Calculate midpoint of the two transit planets
  let midpoint = (transit1Longitude + transit2Longitude) / 2;

  // Handle cases where midpoint crosses 0° Aries
  if (Math.abs(transit1Longitude - transit2Longitude) > 180) {
    midpoint = (midpoint + 180) % 360;
  }

  // Check all aspect types
  for (const aspect of ASPECT_TYPES) {
    const expectedAngle = aspect.angle;
    const actualAngle = getAngularDifference(natalLongitude, midpoint);
    const orb = Math.abs(actualAngle - expectedAngle);

    // Use planet-specific orb for natal-to-midpoint aspects
    const maxOrb = getNatalToMidpointOrb(natalPlanetName, aspect.name);
    if (orb <= maxOrb) {
      return { aspect, orb };
    }
  }

  return null;
};

export const NatalToTransitCalendar: React.FC<NatalToTransitCalendarProps> = ({
  natalDate,
  natalDateUTC: natalDateUTCProp,
  natalLatitude,
  natalLongitude,
  natalTimezone,
  firstHouseReference,
  manualFirstHouseSign = 'Aries',
  cityName = '',
  houseSystem,
  showWeekNavigation = true,
  currentWeekOnly = false,
  allowedYears = [],
  monthlyAccess = false,
  nttWeekly = true,
  nttMonthly = false,
  nttAnnual = false,
  nttWeeklyStartDate,
  nttMonthlyStartDate,
  nttAnnualStartDate,
  nttAnnualEndDate
}) => {
  // Get today's week start as the base week
  const getTodayWeekStart = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(today.setDate(diff));
  };

  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(() => getTodayWeekStart());

  const [expandedDayIndex, setExpandedDayIndex] = useState<number | null>(null);
  const [expandedAspectIndex, setExpandedAspectIndex] = useState<{day: number, aspect: number} | null>(null);
  const [dailyBriefingIndex, setDailyBriefingIndex] = useState<number | null>(null);
  const [showWeeklyBriefing, setShowWeeklyBriefing] = useState(false);
  const [showDisplayOptions, setShowDisplayOptions] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showNatalPositions, setShowNatalPositions] = useState(false);
  const [showLegend, setShowLegend] = useState(false);

  // The birth UTC instant: use the exact prop when supplied (stored profiles);
  // otherwise reconstruct it from natalDate's wall-clock components with a
  // DST-aware two-pass lookup of the birth location's offset.
  const natalDateUTC = useMemo(() => {
    if (natalDateUTCProp) return natalDateUTCProp;
    return new Date(civilToUTC(
      natalDate.getFullYear(), natalDate.getMonth() + 1, natalDate.getDate(),
      natalDate.getHours(), natalDate.getMinutes(), natalDate.getSeconds(),
      natalTimezone
    ));
  }, [natalDateUTCProp, natalDate, natalTimezone]);

  // Calculate natal positions once (using UTC-adjusted date)
  const natalPositions = useMemo(() => {
    const positions = calculatePlanetaryPositions(natalDateUTC);
    return positions;
  }, [natalDateUTC]);

  // Calculate ascendant longitude (returns a number, not an object)
  const ascendantLongitude = useMemo(() => {
    return calculateAscendant(natalDateUTC, natalLatitude, natalLongitude);
  }, [natalDateUTC, natalLatitude, natalLongitude]);

  // Determine first house longitude based on selected reference
  const firstHouseLongitude = useMemo(() => {
    if (firstHouseReference === 'ascendant') {
      return ascendantLongitude;
    } else if (firstHouseReference === 'sun') {
      const sunPos = natalPositions.find(p => p.name === 'Sun');
      return sunPos?.longitude || 0;
    } else if (firstHouseReference === 'moon') {
      const moonPos = natalPositions.find(p => p.name === 'Moon');
      return moonPos?.longitude || 0;
    } else {
      // Manual: convert sign name to longitude (0° of that sign)
      const signIdx = ZODIAC_SIGNS.indexOf(manualFirstHouseSign);
      return signIdx >= 0 ? signIdx * 30 : 0;
    }
  }, [firstHouseReference, ascendantLongitude, natalPositions, manualFirstHouseSign]);

  // Convert first house longitude to zodiac sign
  const ascendantSign = useMemo(() => {
    const signIndex = Math.floor(firstHouseLongitude / 30) % 12;
    return ZODIAC_SIGNS[signIndex];
  }, [firstHouseLongitude]);

  // The 12 house cusps for the selected system.
  // Placidus produces UNEQUAL houses, so every house lookup goes through the
  // cusp array rather than assuming 30° per house.
  const houseCusps = useMemo(() => {
    if (houseSystem === 'placidus' && firstHouseReference === 'ascendant') {
      return calculateHouses(natalDateUTC, natalLatitude, natalLongitude, 'placidus');
    }
    if (houseSystem === 'whole-sign') {
      const firstSign = Math.floor(firstHouseLongitude / 30);
      return Array.from({ length: 12 }, (_, i) => ((firstSign + i) % 12) * 30);
    }
    // Equal houses — also the fallback for Placidus with a non-Ascendant
    // first-house reference, since Placidus is defined from the Ascendant.
    return Array.from({ length: 12 }, (_, i) => (firstHouseLongitude + i * 30) % 360);
  }, [houseSystem, firstHouseReference, natalDateUTC, natalLatitude, natalLongitude, firstHouseLongitude]);

  // Add houses to natal planets
  const natalPlanetsWithHouses = useMemo(() => {
    return natalPositions.map(planet => ({
      ...planet,
      house: getHouseFromCusps(planet.longitude, houseCusps)
    }));
  }, [natalPositions, houseCusps]);

  // Calculate aspects for each day of the week
  const weeklyAspects = useMemo(() => {
    const days = [];

    for (let i = 0; i < 7; i++) {
      const date = new Date(currentWeekStart);
      date.setDate(date.getDate() + i);

      // Calculate transit positions for this day
      const transitPositions = calculatePlanetaryPositions(date);
      const transitPlanetsWithHouses = transitPositions.map(planet => ({
        ...planet,
        house: getHouseFromCusps(planet.longitude, houseCusps)
      }));

      // First, find all transit-to-transit aspects for this day
      // EXCLUDE Moon from transit-to-transit aspects
      // EXCLUDE North Node opposition South Node (always 180°)
      const transitToTransitAspects: any[] = [];
      for (let j = 0; j < transitPlanetsWithHouses.length; j++) {
        for (let k = j + 1; k < transitPlanetsWithHouses.length; k++) {
          const planet1 = transitPlanetsWithHouses[j];
          const planet2 = transitPlanetsWithHouses[k];

          // Skip if either planet is Moon
          if (planet1.name === 'Moon' || planet2.name === 'Moon') {
            continue;
          }

          // Skip North Node opposition South Node (always opposite)
          const isNodePair = (
            (planet1.name === 'North Node' && planet2.name === 'South Node') ||
            (planet1.name === 'South Node' && planet2.name === 'North Node')
          );
          if (isNodePair) {
            continue;
          }

          for (const aspectType of ASPECT_TYPES) {
            const angDiff = getAngularDifference(planet1.longitude, planet2.longitude);
            const orbDiff = Math.abs(angDiff - aspectType.angle);
            const maxOrb = getTransitToTransitOrb(planet1.name, planet2.name, aspectType.name);

            if (orbDiff <= maxOrb) {
              transitToTransitAspects.push({
                transit1: planet1,
                transit2: planet2,
                aspect: {
                  ...aspectType,
                  actualOrb: orbDiff
                }
              });
            }
          }
        }
      }

      // Now check if any natal planet aspects the midpoint of these transit-to-transit aspects
      const natalToTransitAspects: any[] = [];

      for (const transitAspect of transitToTransitAspects) {
        for (const natalPlanet of natalPlanetsWithHouses) {
          const aspectCheck = checkNatalToTransitAspect(
            natalPlanet.name,
            natalPlanet.longitude,
            transitAspect.transit1.longitude,
            transitAspect.transit2.longitude
          );

          if (aspectCheck) {
            natalToTransitAspects.push({
              natal: natalPlanet,
              transitAspect: transitAspect,
              aspect: {
                ...aspectCheck.aspect,
                actualOrb: aspectCheck.orb
              }
            });
          }
        }
      }

      // For any natal-to-transit activation involving a node, generate the companion entry
      // e.g. if natal Mercury sextile (Mars opp South Node) exists, also show Mercury trine (Mars conj North Node)
      const COMPLEMENT_ASPECT_MAP: Record<string, string> = {
        'Conjunction': 'Opposition', 'Opposition': 'Conjunction',
        'Trine': 'Sextile', 'Sextile': 'Trine',
        'Square': 'Square'
      };

      const nodeCompanionActivations: typeof natalToTransitAspects = [];
      for (const activation of natalToTransitAspects) {
        const ta = activation.transitAspect;
        const t1IsNode = ta.transit1.name === 'North Node' || ta.transit1.name === 'South Node';
        const t2IsNode = ta.transit2.name === 'North Node' || ta.transit2.name === 'South Node';
        if (!t1IsNode && !t2IsNode) continue;

        const complementTransitAspectName = COMPLEMENT_ASPECT_MAP[ta.aspect.name];
        if (!complementTransitAspectName) continue;
        const complementTransitAspectType = ASPECT_TYPES.find(a => a.name === complementTransitAspectName);
        if (!complementTransitAspectType) continue;

        const complementNatalAspectName = COMPLEMENT_ASPECT_MAP[activation.aspect.name];
        if (!complementNatalAspectName) continue;
        const complementNatalAspectType = ASPECT_TYPES.find(a => a.name === complementNatalAspectName);
        if (!complementNatalAspectType) continue;

        const nodeInAspect = t1IsNode ? ta.transit1 : ta.transit2;
        const nonNode = t1IsNode ? ta.transit2 : ta.transit1;
        const otherNodeName = nodeInAspect.name === 'North Node' ? 'South Node' : 'North Node';
        const otherNode = transitPlanetsWithHouses.find(p => p.name === otherNodeName);
        if (!otherNode) continue;

        const alreadyExists = natalToTransitAspects.some(existing =>
          existing.natal.name === activation.natal.name &&
          ((existing.transitAspect.transit1.name === nonNode.name && existing.transitAspect.transit2.name === otherNodeName) ||
           (existing.transitAspect.transit1.name === otherNodeName && existing.transitAspect.transit2.name === nonNode.name)) &&
          existing.transitAspect.aspect.name === complementTransitAspectName
        ) || nodeCompanionActivations.some(existing =>
          existing.natal.name === activation.natal.name &&
          ((existing.transitAspect.transit1.name === nonNode.name && existing.transitAspect.transit2.name === otherNodeName) ||
           (existing.transitAspect.transit1.name === otherNodeName && existing.transitAspect.transit2.name === nonNode.name)) &&
          existing.transitAspect.aspect.name === complementTransitAspectName
        );
        if (alreadyExists) continue;

        const transitOrbDiff = Math.abs(getAngularDifference(nonNode.longitude, otherNode.longitude) - complementTransitAspectType.angle);

        nodeCompanionActivations.push({
          natal: activation.natal,
          transitAspect: {
            transit1: nonNode,
            transit2: otherNode,
            aspect: {
              ...complementTransitAspectType,
              actualOrb: transitOrbDiff
            }
          },
          aspect: {
            ...complementNatalAspectType,
            actualOrb: activation.aspect.actualOrb
          }
        });
      }
      natalToTransitAspects.push(...nodeCompanionActivations);

      // Sort by planet speed (fastest to slowest: Moon, Sun, Mercury, Venus, Mars, Jupiter, Saturn, Uranus, Neptune, Pluto, Nodes)
      natalToTransitAspects.sort((a, b) => {
        const speedA = PLANET_SPEED_ORDER[a.natal.name] || 99;
        const speedB = PLANET_SPEED_ORDER[b.natal.name] || 99;
        return speedA - speedB;
      });

      days.push({
        date,
        aspects: natalToTransitAspects
      });
    }

    return days;
  }, [currentWeekStart, firstHouseLongitude, natalPlanetsWithHouses, houseSystem, houseCusps]);

  // Create a consistent ordering for transit-to-transit aspects across the week
  // This ensures the same transit aspect appears on the same row across multiple days
  const transitAspectRowMapping = useMemo(() => {
    const uniqueTransitAspects = new Map<string, number>();
    let rowIndex = 0;

    // Collect all unique transit-to-transit aspects from the entire week
    for (const day of weeklyAspects) {
      for (const aspect of day.aspects) {
        // Create a unique key for this transit-to-transit aspect
        const planet1 = aspect.transitAspect.transit1.name;
        const planet2 = aspect.transitAspect.transit2.name;
        const aspectType = aspect.transitAspect.aspect.name;

        // Ensure consistent ordering of planets in the key
        const [p1, p2] = planet1 < planet2 ? [planet1, planet2] : [planet2, planet1];
        const key = `${p1}-${aspectType}-${p2}`;

        if (!uniqueTransitAspects.has(key)) {
          uniqueTransitAspects.set(key, rowIndex++);
        }
      }
    }

    return uniqueTransitAspects;
  }, [weeklyAspects]);

  // Reorganize aspects for each day based on the row mapping
  // Add rowIndex to each aspect for visual alignment
  const alignedWeeklyAspects = useMemo(() => {
    return weeklyAspects.map(day => {
      // Create a map of row positions to aspects for this day
      const rowMap = new Map<number, any[]>();

      for (const aspect of day.aspects) {
        const planet1 = aspect.transitAspect.transit1.name;
        const planet2 = aspect.transitAspect.transit2.name;
        const aspectType = aspect.transitAspect.aspect.name;

        const [p1, p2] = planet1 < planet2 ? [planet1, planet2] : [planet2, planet1];
        const key = `${p1}-${aspectType}-${p2}`;

        const rowIndex = transitAspectRowMapping.get(key) || 0;

        if (!rowMap.has(rowIndex)) {
          rowMap.set(rowIndex, []);
        }
        // Add rowIndex metadata to aspect
        rowMap.get(rowIndex)!.push({
          ...aspect,
          _rowIndex: rowIndex,
          _transitKey: key
        });
      }

      // Convert to sorted array maintaining row order
      const sortedAspects: any[] = [];

      // Get all row indices that exist across the entire week (not just this day)
      const allRowIndices = Array.from(transitAspectRowMapping.values()).sort((a, b) => a - b);

      for (const rowIndex of allRowIndices) {
        const aspectsInRow = rowMap.get(rowIndex);
        if (aspectsInRow && aspectsInRow.length > 0) {
          // Sort by natal planet speed within the same row
          aspectsInRow.sort((a, b) => {
            const speedA = PLANET_SPEED_ORDER[a.natal.name] || 99;
            const speedB = PLANET_SPEED_ORDER[b.natal.name] || 99;
            return speedA - speedB;
          });
          sortedAspects.push(...aspectsInRow);
        }
      }

      return {
        ...day,
        aspects: sortedAspects
      };
    });
  }, [weeklyAspects, transitAspectRowMapping]);

  // Calculate allowed date range based on permissions and subscription start dates
  const getMaxAllowedDate = () => {
    const todayWeekStart = getTodayWeekStart();
    const now = new Date();

    // Helper function to get week start (Monday) for any date
    const getWeekStart = (date: Date) => {
      const d = new Date(date);
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1);
      return new Date(d.setDate(diff));
    };

    // Helper function to get week end (Sunday) for any date
    const getWeekEnd = (date: Date) => {
      const weekStart = getWeekStart(date);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      return weekEnd;
    };

    // ANNUAL ACCESS
    if (nttAnnual) {
      if (nttAnnualEndDate) {
        // Bought for a calendar year: open until the end of that year (completing its last week)
        const endDate = new Date(`${nttAnnualEndDate}T23:59:59.999`);
        if (now > endDate) {
          const maxDate = new Date(todayWeekStart);
          maxDate.setDate(maxDate.getDate() + 6);
          return maxDate;
        }
        return getWeekEnd(endDate);
      } else if (nttAnnualStartDate) {
        // With start date: Use subscription-based calculation
        const startDate = new Date(nttAnnualStartDate);

        // Check if subscription has expired
        const expiryDate = new Date(startDate);
        expiryDate.setDate(expiryDate.getDate() + 365);

        if (now > expiryDate) {
          // Subscription expired - revert to default one week
          const maxDate = new Date(todayWeekStart);
          maxDate.setDate(maxDate.getDate() + 6);
          return maxDate;
        }

        // Calculate end date: 365 days + complete the week
        const maxDate = new Date(startDate);
        maxDate.setDate(maxDate.getDate() + 365);
        // Extend to end of that week (Sunday)
        return getWeekEnd(maxDate);
      } else {
        // FALLBACK: No start date but permission granted - allow full year from today
        const maxDate = new Date(now);
        maxDate.setDate(maxDate.getDate() + 365);
        return getWeekEnd(maxDate);
      }
    }

    // MONTHLY ACCESS
    else if (nttMonthly) {
      if (nttMonthlyStartDate) {
        // With start date: Use subscription-based calculation
        const startDate = new Date(nttMonthlyStartDate);

        // Check if subscription has expired
        const expiryDate = new Date(startDate);
        expiryDate.setDate(expiryDate.getDate() + 30);

        if (now > expiryDate) {
          // Subscription expired - revert to default one week
          const maxDate = new Date(todayWeekStart);
          maxDate.setDate(maxDate.getDate() + 6);
          return maxDate;
        }

        // Calculate end date: 30 days + complete the week
        const maxDate = new Date(startDate);
        maxDate.setDate(maxDate.getDate() + 30);
        // Extend to end of that week (Sunday)
        return getWeekEnd(maxDate);
      } else {
        // FALLBACK: No start date but permission granted - allow 30 days from today
        const maxDate = new Date(now);
        maxDate.setDate(maxDate.getDate() + 30);
        return getWeekEnd(maxDate);
      }
    }

    // WEEKLY ACCESS
    else if (nttWeekly) {
      if (nttWeeklyStartDate) {
        // With start date: Use subscription-based calculation
        const startDate = new Date(nttWeeklyStartDate);

        // Check if subscription has expired
        const expiryDate = new Date(startDate);
        expiryDate.setDate(expiryDate.getDate() + 7);

        if (now > expiryDate) {
          // Subscription expired - revert to default one week
          const maxDate = new Date(todayWeekStart);
          maxDate.setDate(maxDate.getDate() + 6);
          return maxDate;
        }

        // Calculate end date: 7 days + complete the week
        const maxDate = new Date(startDate);
        maxDate.setDate(maxDate.getDate() + 7);
        // Extend to end of that week (Sunday)
        return getWeekEnd(maxDate);
      } else {
        // FALLBACK: No start date but permission granted - allow current + next week
        const maxDate = new Date(todayWeekStart);
        maxDate.setDate(maxDate.getDate() + 13); // Current week + next week (14 days total)
        return maxDate;
      }
    }

    // Default: current week only (Monday to Sunday)
    const maxDate = new Date(todayWeekStart);
    maxDate.setDate(maxDate.getDate() + 6); // End of current week (Sunday)
    return maxDate;
  };

  const canNavigateNext = () => {
    const nextWeekStart = new Date(currentWeekStart);
    nextWeekStart.setDate(nextWeekStart.getDate() + 7);
    const maxAllowedDate = getMaxAllowedDate();
    return nextWeekStart <= maxAllowedDate;
  };

  const canNavigatePrevious = () => {
    const todayWeekStart = getTodayWeekStart();
    return currentWeekStart > todayWeekStart;
  };

  const handlePreviousWeek = () => {
    if (!canNavigatePrevious()) return;
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() - 7);
    setCurrentWeekStart(newStart);
    setExpandedDayIndex(null);
    setExpandedAspectIndex(null);
  };

  const handleNextWeek = () => {
    if (!canNavigateNext()) return;
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() + 7);
    setCurrentWeekStart(newStart);
    setExpandedDayIndex(null);
    setExpandedAspectIndex(null);
  };

  const handleToday = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    setCurrentWeekStart(new Date(today.setDate(diff)));
    setExpandedDayIndex(null);
    setExpandedAspectIndex(null);
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto', color: '#ede8e0' }}>
      <h2 style={{ textAlign: 'center', marginBottom: '20px', color: '#ede8e0', fontSize: '22px' }}>
        Your Personal Cosmic Activation
      </h2>

      {/* About This Calendar — Collapsible (dark banner matching Display Options) */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        padding: '14px 20px',
        borderRadius: '12px',
        marginBottom: '10px',
        border: '1px solid rgba(138, 43, 226, 0.3)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
      }}>
        <div
          onClick={() => setShowAbout(!showAbout)}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', userSelect: 'none' }}
        >
          <span style={{ fontSize: '15px', fontWeight: 600, color: '#fff' }}>About This Calendar</span>
          <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', transition: 'transform 0.2s', transform: showAbout ? 'rotate(180deg)' : 'rotate(0deg)', display: 'inline-block' }}>▼</span>
        </div>
        {showAbout && (
          <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6', color: 'rgba(255,255,255,0.85)' }}>
              Shows when your natal planets aspect the midpoint of transit-to-transit configurations.
              These are powerful activation points where your birth chart themes trigger and color current planetary dynamics.
            </p>
            <p style={{ margin: '8px 0 0 0', fontSize: '14px', lineHeight: '1.6', color: 'rgba(255,255,255,0.85)' }}>
              <strong style={{ color: '#ffd700' }}>Birth Chart:</strong> {natalDate.toLocaleDateString()} • {cityName || `${natalLatitude.toFixed(4)}°, ${natalLongitude.toFixed(4)}°`}
            </p>
            <div
              onClick={(e) => { e.stopPropagation(); setShowDisplayOptions(!showDisplayOptions); }}
              style={{ margin: '10px 0 0 0', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, color: '#ffd700', userSelect: 'none' }}
            >
              <span style={{ transform: showDisplayOptions ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s', display: 'inline-block', fontSize: '10px' }}>&#9654;</span>
              Display Options
            </div>
            {showDisplayOptions && (
              <div style={{ marginTop: '8px', paddingLeft: '16px' }}>
                <p style={{ margin: '4px 0', fontSize: '13px', lineHeight: '1.6', color: 'rgba(255,255,255,0.8)' }}>
                  <strong style={{ color: '#ffd700' }}>Ascendant Reference:</strong> {firstHouseReference === 'ascendant' ? `Ascendant (${ascendantSign})` : firstHouseReference === 'sun' ? 'Sun Sign' : firstHouseReference === 'moon' ? 'Moon Sign' : `Manual (${manualFirstHouseSign})`}
                </p>
                {firstHouseReference === 'ascendant' && (
                  <p style={{ margin: '4px 0', fontSize: '12px', lineHeight: '1.6', color: 'rgba(255,255,255,0.6)' }}>
                    <strong>Calculated Ascendant:</strong> {ascendantSign} (Longitude: {ascendantLongitude.toFixed(2)}°)
                  </p>
                )}
                <p style={{ margin: '4px 0', fontSize: '12px', lineHeight: '1.6', color: 'rgba(255,255,255,0.6)' }}>
                  <strong>1st House Cusp:</strong> {ascendantSign} {Math.floor(firstHouseLongitude % 30)}° (Longitude: {firstHouseLongitude.toFixed(2)}°)
                </p>
                <p style={{ margin: '4px 0', fontSize: '12px', lineHeight: '1.6', color: 'rgba(255,255,255,0.6)' }}>
                  <strong>House System:</strong> {houseSystem === 'whole-sign' ? 'Whole Sign' : houseSystem === 'placidus' ? (firstHouseReference === 'ascendant' ? 'Placidus' : 'Equal House (Placidus needs an Ascendant first house)') : 'Equal House'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Natal Chart Planetary Positions — Collapsible (dark banner matching Display Options) */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        padding: '14px 20px',
        borderRadius: '12px',
        marginBottom: '10px',
        border: '1px solid rgba(138, 43, 226, 0.3)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
      }}>
        <div
          onClick={() => setShowNatalPositions(!showNatalPositions)}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', userSelect: 'none' }}
        >
          <span style={{ fontSize: '15px', fontWeight: 600, color: '#fff' }}>Your Natal Chart Planetary Positions</span>
          <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', transition: 'transform 0.2s', transform: showNatalPositions ? 'rotate(180deg)' : 'rotate(0deg)', display: 'inline-block' }}>▼</span>
        </div>
        {showNatalPositions && (
          <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '8px',
              fontSize: '13px'
            }}>
              {natalPlanetsWithHouses.map(planet => (
                <div
                  key={planet.name}
                  style={{
                    padding: '8px 10px',
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    borderRadius: '6px',
                    border: '1px solid rgba(138,43,226,0.2)'
                  }}
                >
                  <strong style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px' }}>{PLANET_SYMBOLS[planet.name]} {planet.name}:</strong>
                  <br />
                  <span style={{ fontSize: '14px', color: '#fff', fontWeight: '600' }}>
                    {planet.sign} {Math.floor(planet.degreeInSign)}°{Math.floor((planet.degreeInSign % 1) * 60)}'
                  </span>
                  <br />
                  <span style={{ fontSize: '12px', color: '#ffd700', fontWeight: 'bold' }}>
                    {getOrdinal(planet.house)} House
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Week Navigation */}
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
        <button
          onClick={handlePreviousWeek}
          disabled={!canNavigatePrevious()}
          style={{
            padding: '10px 20px',
            fontSize: '14px',
            backgroundColor: canNavigatePrevious() ? '#4a90e2' : '#cccccc',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: canNavigatePrevious() ? 'pointer' : 'not-allowed',
            opacity: canNavigatePrevious() ? 1 : 0.6
          }}
        >
          ← Previous Week
        </button>
        <button
          onClick={handleToday}
          style={{
            padding: '10px 20px',
            fontSize: '14px',
            backgroundColor: '#2ecc71',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }}
        >
          This Week
        </button>
        <button
          onClick={handleNextWeek}
          disabled={!canNavigateNext()}
          style={{
            padding: '10px 20px',
            fontSize: '14px',
            backgroundColor: canNavigateNext() ? '#4a90e2' : '#cccccc',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: canNavigateNext() ? 'pointer' : 'not-allowed',
            opacity: canNavigateNext() ? 1 : 0.6
          }}
        >
          Next Week →
        </button>
        <button
          onClick={() => {
            setShowWeeklyBriefing(!showWeeklyBriefing);
            setDailyBriefingIndex(null);
            setExpandedAspectIndex(null);
          }}
          style={{
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: 'bold',
            backgroundColor: showWeeklyBriefing ? '#1a237e' : '#7c4dff',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            letterSpacing: '0.5px'
          }}
        >
          Weekly Briefing
        </button>
      </div>

      {/* Weekly Briefing Panel */}
      {showWeeklyBriefing && weeklyAspects.length > 0 && (() => {
        const weeklyInput = weeklyAspects.map((day: any) => ({
          date: day.date,
          activations: day.aspects.map((a: any) => ({
            natalPlanet: a.natal.name,
            natalHouse: a.natal.house,
            transit1Planet: a.transitAspect.transit1.name,
            transit1House: a.transitAspect.transit1.house,
            transit2Planet: a.transitAspect.transit2.name,
            transit2House: a.transitAspect.transit2.house,
            natalAspectType: a.aspect.name,
            transitAspectType: a.transitAspect.aspect.name,
            orb: a.aspect.actualOrb
          }))
        }));
        const ws = generateWeeklySummary(weeklyInput);

        const intensityColorMap: Record<string, string> = {
          'Quiet': '#66bb6a', 'Active': '#42a5f5', 'Intense': '#ffa726', 'Pivotal': '#ef5350'
        };

        return (
          <div style={{
            marginBottom: '25px',
            padding: '25px',
            backgroundColor: '#12121f',
            borderRadius: '12px',
            border: '3px solid #7c4dff',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
              <div>
                <h3 style={{ margin: 0, color: '#ede8e0', fontSize: '20px' }}>
                  Weekly Cosmic Briefing
                </h3>
                <div style={{ fontSize: '14px', color: '#8a8a9a', marginTop: '4px' }}>
                  {weeklyAspects[0].date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} — {weeklyAspects[6].date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </div>
              </div>
              <button
                onClick={() => setShowWeeklyBriefing(false)}
                style={{
                  padding: '6px 16px',
                  backgroundColor: '#666',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                Close
              </button>
            </div>

            {/* Overview */}
            <div style={{
              padding: '14px 16px',
              backgroundColor: 'rgba(124,77,255,0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(124,77,255,0.3)',
              marginBottom: '20px',
              fontSize: '14px',
              lineHeight: '1.6',
              color: '#ede8e0'
            }}>
              {ws.overview}
            </div>

            {/* Intensity Timeline */}
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', color: '#ede8e0' }}>
                Weekly Intensity Timeline
              </h4>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-end' }}>
                {ws.dailyIntensities.map((d, i) => (
                  <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{
                      height: `${Math.max(d.intensityScore * 18, 8)}px`,
                      backgroundColor: intensityColorMap[d.intensity],
                      borderRadius: '4px 4px 0 0',
                      transition: 'height 0.3s',
                      marginBottom: '4px',
                      position: 'relative'
                    }}>
                      {d.activationCount > 0 && (
                        <div style={{
                          position: 'absolute',
                          top: '-18px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          color: intensityColorMap[d.intensity]
                        }}>
                          {d.activationCount}
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#8a8a9a' }}>
                      {d.dayName.slice(0, 3)}
                    </div>
                    <div style={{ fontSize: '10px', color: '#999' }}>
                      {d.dateLabel}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '12px', color: '#777' }}>
                <span>Peak: <strong style={{ color: '#fbbf24' }}>{ws.peakDay.dayName}</strong></span>
                <span>Rest: <strong style={{ color: '#4ade80' }}>{ws.restDay.dayName}</strong></span>
              </div>
            </div>

            {/* Weekly Life Event Radar */}
            {ws.weeklyAlerts.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', color: '#ede8e0' }}>
                  🔮 Weekly Life Event Radar
                </h4>
                {ws.weeklyAlerts.map((alert, i) => (
                  <div key={i} style={{
                    padding: '12px 16px',
                    backgroundColor: alert.type === 'money' ? 'rgba(255,193,7,0.1)' : alert.type === 'love' ? 'rgba(233,30,99,0.1)' : alert.type === 'career' ? 'rgba(63,81,181,0.1)' : alert.type === 'property' ? 'rgba(0,150,136,0.1)' : alert.type === 'travel' ? 'rgba(3,169,244,0.1)' : 'rgba(156,39,176,0.1)',
                    borderRadius: '8px',
                    border: `2px solid ${alert.type === 'money' ? '#ffc107' : alert.type === 'love' ? '#e91e63' : alert.type === 'career' ? '#3f51b5' : alert.type === 'property' ? '#009688' : alert.type === 'travel' ? '#03a9f4' : '#9c27b0'}`,
                    marginBottom: '10px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px'
                  }}>
                    <span style={{ fontSize: '22px', lineHeight: '1' }}>{alert.emoji}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <span style={{ fontWeight: 'bold', fontSize: '14px', color: '#ede8e0' }}>
                          {alert.type.charAt(0).toUpperCase() + alert.type.slice(1)} Week
                        </span>
                        <span style={{ fontSize: '11px', color: '#888', fontWeight: 'normal' }}>
                          {alert.daysActive} days active • peaks {alert.peakDay}
                        </span>
                      </div>
                      <div style={{ fontSize: '13px', color: '#8a8a9a', lineHeight: '1.5' }}>
                        {alert.message}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Major Themes */}
            {ws.majorThemes.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', color: '#ede8e0' }}>
                  🌊 Dominant Energies
                </h4>
                {ws.majorThemes.map((theme, i) => (
                  <div key={i} style={{
                    padding: '12px 16px',
                    backgroundColor: theme.isDominant ? 'rgba(124,77,255,0.1)' : 'rgba(255,255,255,0.05)',
                    borderRadius: '8px',
                    border: theme.isDominant ? '2px solid #7c4dff' : '1px solid rgba(201,168,124,0.12)',
                    marginBottom: '8px',
                    borderLeft: `4px solid ${theme.hardCount > theme.softCount ? '#ff9800' : theme.softCount > theme.hardCount ? '#4caf50' : '#7c4dff'}`
                  }}>
                    <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#ede8e0', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {theme.transitPair} — {theme.theme}
                      {theme.isDominant && (
                        <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#fff', backgroundColor: '#7c4dff', padding: '2px 6px', borderRadius: '4px' }}>
                          SIGNATURE ENERGY
                        </span>
                      )}
                      <span style={{ fontSize: '11px', fontWeight: 'normal', color: '#888', marginLeft: 'auto' }}>
                        {theme.daysActive} day{theme.daysActive > 1 ? 's' : ''}
                      </span>
                    </div>
                    <div style={{ fontSize: '13px', color: '#8a8a9a', lineHeight: '1.5' }}>
                      {theme.summary}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Houses Under the Spotlight */}
            {ws.houseSpotlights.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', color: '#ede8e0' }}>
                  🏠 Life Areas in Focus
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: ws.houseSpotlights.length >= 3 ? '1fr 1fr 1fr' : ws.houseSpotlights.length === 2 ? '1fr 1fr' : '1fr', gap: '10px' }}>
                  {ws.houseSpotlights.map((h, i) => (
                    <div key={i} style={{
                      padding: '12px 14px',
                      backgroundColor: '#1a1a2e',
                      borderRadius: '8px',
                      border: '1px solid rgba(201,168,124,0.12)',
                      borderLeft: `4px solid ${h.hardCount > h.softCount ? '#ff9800' : h.softCount > h.hardCount ? '#4caf50' : '#5c6bc0'}`
                    }}>
                      <div style={{ fontWeight: 'bold', fontSize: '13px', color: '#c9a87c', marginBottom: '4px' }}>
                        {h.domainLabel}
                        <span style={{ fontSize: '11px', fontWeight: 'normal', color: '#888', display: 'block' }}>
                          {h.house}{['th','st','nd','rd'][h.house % 100 > 3 && h.house % 100 < 21 ? 0 : h.house % 10 > 3 ? 0 : h.house % 10] || 'th'} house • {h.totalActivations} hits
                        </span>
                      </div>
                      <div style={{ fontSize: '12px', color: '#8a8a9a', lineHeight: '1.5' }}>
                        {h.arc}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Key Days */}
            {ws.keyDays.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', color: '#ede8e0' }}>
                  📅 Week at a Glance
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {ws.keyDays.map((kd, i) => (
                    <div key={i} style={{
                      padding: '10px 14px',
                      backgroundColor: kd.type === 'power' ? 'rgba(63,81,181,0.1)' : kd.type === 'caution' ? 'rgba(255,152,0,0.1)' : 'rgba(76,175,80,0.1)',
                      borderRadius: '8px',
                      border: `1px solid ${kd.type === 'power' ? '#7c4dff' : kd.type === 'caution' ? '#ffcc80' : '#a5d6a7'}`,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px'
                    }}>
                      <div style={{ fontWeight: 'bold', fontSize: '13px', color: kd.type === 'power' ? '#818cf8' : kd.type === 'caution' ? '#fbbf24' : '#4ade80', minWidth: '120px' }}>
                        {kd.label}
                        <div style={{ fontSize: '11px', fontWeight: 'normal', color: '#888' }}>
                          {kd.dayName} ({kd.dateLabel})
                        </div>
                      </div>
                      <div style={{ fontSize: '13px', color: '#8a8a9a', lineHeight: '1.5' }}>
                        {kd.description}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Biggest Challenge & Opportunity side by side */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
              <div style={{
                padding: '14px 16px',
                backgroundColor: 'rgba(255,152,0,0.1)',
                borderRadius: '8px',
                border: '1px solid #ffcc80'
              }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#fbbf24' }}>
                  ⚡ Week's Biggest Challenge
                </h4>
                <div style={{ fontSize: '13px', color: '#fbbf24', lineHeight: '1.6' }}>
                  {ws.biggestChallenge}
                </div>
              </div>
              <div style={{
                padding: '14px 16px',
                backgroundColor: 'rgba(76,175,80,0.1)',
                borderRadius: '8px',
                border: '1px solid #a5d6a7'
              }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#4ade80' }}>
                  ✨ Week's Biggest Opportunity
                </h4>
                <div style={{ fontSize: '13px', color: '#4ade80', lineHeight: '1.6' }}>
                  {ws.biggestOpportunity}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Calendar Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: '10px',
        marginTop: '20px'
      }}>
        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day, idx) => (
          <div
            key={idx}
            style={{
              padding: '10px',
              backgroundColor: '#1a1a2e',
              borderRadius: '5px 5px 0 0',
              fontWeight: 'bold',
              textAlign: 'center',
              fontSize: '14px',
              color: '#ede8e0',
              borderBottom: '1px solid rgba(201,168,124,0.15)'
            }}
          >
            {day}
          </div>
        ))}

        {alignedWeeklyAspects.map((day, dayIdx) => {
          const isToday = day.date.toDateString() === new Date().toDateString();
          const isExpanded = expandedDayIndex === dayIdx;

          return (
            <div
              key={dayIdx}
              style={{
                border: isToday ? '2px solid #4ade80' : '1px solid rgba(201,168,124,0.12)',
                borderRadius: '8px',
                padding: '10px',
                minHeight: '120px',
                backgroundColor: '#1a1a2e',
                position: 'relative'
              }}
            >
              <div
                style={{
                  fontSize: '14px',
                  fontWeight: 'bold',
                  marginBottom: '8px',
                  color: isToday ? '#4ade80' : '#ede8e0'
                }}
              >
                {day.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </div>

              {day.aspects.length > 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDailyBriefingIndex(dailyBriefingIndex === dayIdx ? null : dayIdx);
                    setExpandedAspectIndex(null);
                  }}
                  style={{
                    width: '100%',
                    padding: '4px 8px',
                    marginBottom: '6px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    backgroundColor: dailyBriefingIndex === dayIdx ? '#1a237e' : '#3f51b5',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    letterSpacing: '0.5px'
                  }}
                >
                  Daily Briefing
                </button>
              )}

              {day.aspects.length > 0 ? (
                <div>
                  <div style={{ fontSize: '12px', color: '#8a8a9a', marginBottom: '5px' }}>
                    {day.aspects.length} activation{day.aspects.length !== 1 ? 's' : ''}
                  </div>

                  {/* Show top 15 aspects inline */}
                  {day.aspects.slice(0, 15).map((aspect, aspectIdx) => {
                    // Check if this is a new transit group (different from previous aspect)
                    const prevAspect = aspectIdx > 0 ? day.aspects[aspectIdx - 1] : null;
                    const isNewTransitGroup = !prevAspect || prevAspect._transitKey !== aspect._transitKey;

                    return (
                      <React.Fragment key={aspectIdx}>
                        {/* Show transit group divider for new transit types */}
                        {isNewTransitGroup && aspectIdx > 0 && (
                          <div style={{
                            height: '1px',
                            backgroundColor: 'rgba(201,168,124,0.15)',
                            margin: '6px 0'
                          }} />
                        )}
                        <div
                          onClick={() => {
                            if (expandedAspectIndex?.day === dayIdx && expandedAspectIndex?.aspect === aspectIdx) {
                              setExpandedAspectIndex(null);
                            } else {
                              setExpandedAspectIndex({ day: dayIdx, aspect: aspectIdx });
                              setExpandedDayIndex(dayIdx);
                            }
                          }}
                          style={{
                            fontSize: '11px',
                            padding: '4px',
                            marginBottom: '3px',
                            backgroundColor: `${PLANET_COLORS[aspect.natal.name]}40`,
                            borderRadius: '3px',
                            cursor: 'pointer',
                            color: '#ede8e0',
                            border: expandedAspectIndex?.day === dayIdx && expandedAspectIndex?.aspect === aspectIdx
                              ? `3px solid ${aspect.aspect.color}`
                              : `2px solid ${aspect.aspect.color}80`
                          }}
                          title={`Orb: ${aspect.aspect.actualOrb.toFixed(2)}°`}
                        >
                          {PLANET_SYMBOLS[aspect.natal.name]} →
                          ({PLANET_SYMBOLS[aspect.transitAspect.transit1.name]} {aspect.transitAspect.aspect.symbol} {PLANET_SYMBOLS[aspect.transitAspect.transit2.name]})
                        </div>
                      </React.Fragment>
                    );
                  })}

                  {day.aspects.length > 15 && (
                    <div
                      onClick={() => setExpandedDayIndex(isExpanded ? null : dayIdx)}
                      style={{
                        fontSize: '11px',
                        color: '#4a90e2',
                        cursor: 'pointer',
                        marginTop: '5px',
                        textDecoration: 'underline'
                      }}
                    >
                      {isExpanded ? 'Show less' : `+${day.aspects.length - 15} more`}
                    </div>
                  )}

                  {/* Expanded view of all aspects for this day */}
                  {isExpanded && day.aspects.length > 15 && (
                    <div style={{ marginTop: '10px' }}>
                      {day.aspects.slice(15).map((aspect, aspectIdx) => {
                        // Check if this is a new transit group (different from previous aspect)
                        const fullIdx = aspectIdx + 15;
                        const prevAspect = fullIdx > 0 ? day.aspects[fullIdx - 1] : null;
                        const isNewTransitGroup = !prevAspect || prevAspect._transitKey !== aspect._transitKey;

                        return (
                          <React.Fragment key={fullIdx}>
                            {/* Show transit group divider for new transit types */}
                            {isNewTransitGroup && fullIdx > 15 && (
                              <div style={{
                                height: '1px',
                                backgroundColor: 'rgba(201,168,124,0.15)',
                                margin: '6px 0'
                              }} />
                            )}
                            <div
                              onClick={() => {
                                if (expandedAspectIndex?.day === dayIdx && expandedAspectIndex?.aspect === fullIdx) {
                                  setExpandedAspectIndex(null);
                                } else {
                                  setExpandedAspectIndex({ day: dayIdx, aspect: fullIdx });
                                }
                              }}
                              style={{
                                fontSize: '11px',
                                padding: '4px',
                                marginBottom: '3px',
                                backgroundColor: `${PLANET_COLORS[aspect.natal.name]}40`,
                                borderRadius: '3px',
                                cursor: 'pointer',
                                border: expandedAspectIndex?.day === dayIdx && expandedAspectIndex?.aspect === fullIdx
                                  ? `3px solid ${aspect.aspect.color}`
                                  : `2px solid ${aspect.aspect.color}80`
                              }}
                              title={`Orb: ${aspect.aspect.actualOrb.toFixed(2)}°`}
                            >
                              {PLANET_SYMBOLS[aspect.natal.name]} →
                              ({PLANET_SYMBOLS[aspect.transitAspect.transit1.name]} {aspect.transitAspect.aspect.symbol} {PLANET_SYMBOLS[aspect.transitAspect.transit2.name]})
                            </div>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: '12px', color: '#999', fontStyle: 'italic' }}>
                  No activations
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Aspect Interpretation Panel */}
      {expandedAspectIndex !== null && alignedWeeklyAspects[expandedAspectIndex.day] && (
        <div style={{
          marginTop: '30px',
          padding: '20px',
          backgroundColor: '#12121f',
          borderRadius: '12px',
          border: '2px solid #4a90e2',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
        }}>
          {(() => {
            const aspect = alignedWeeklyAspects[expandedAspectIndex.day].aspects[expandedAspectIndex.aspect];
            const date = alignedWeeklyAspects[expandedAspectIndex.day].date;

            // Get activation manifestations
            const manifestations = getNatalActivationManifestations(
              aspect.natal.name,
              aspect.natal.house,
              aspect.transitAspect.transit1.name,
              aspect.transitAspect.transit1.house,
              aspect.transitAspect.transit2.name,
              aspect.transitAspect.transit2.house,
              aspect.aspect.name,
              aspect.transitAspect.aspect.name
            );

            return (
              <>
                <h3 style={{ marginTop: 0, color: '#4a90e2' }}>
                  {date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                </h3>

                <div style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  marginBottom: '15px',
                  color: aspect.aspect.color
                }}>
                  {PLANET_SYMBOLS[aspect.natal.name]} {aspect.natal.name} ({getOrdinal(aspect.natal.house)} house)
                  {' '}{aspect.aspect.symbol}{' '}
                  ({PLANET_SYMBOLS[aspect.transitAspect.transit1.name]} {aspect.transitAspect.transit1.name}
                  {' '}{aspect.transitAspect.aspect.symbol}{' '}
                  {PLANET_SYMBOLS[aspect.transitAspect.transit2.name]} {aspect.transitAspect.transit2.name})
                </div>

                <div style={{
                  fontSize: '12px',
                  color: '#8a8a9a',
                  marginBottom: '15px'
                }}>
                  Orb: {aspect.aspect.actualOrb.toFixed(2)}°
                </div>

                <div style={{
                  whiteSpace: 'pre-wrap',
                  lineHeight: '1.6',
                  fontSize: '14px'
                }}>
                  <strong>Natal Planet Activation:</strong>
                  <br />
                  Your natal {aspect.natal.name} in the {getOrdinal(aspect.natal.house)} house is forming a {aspect.aspect.name.toLowerCase()} to a transit-to-transit aspect configuration. This means your natal {aspect.natal.name} is activating and coloring the dynamic between transiting {aspect.transitAspect.transit1.name} and {aspect.transitAspect.transit2.name}.

                  <br /><br />
                  <strong>The Transit Aspect Being Activated:</strong>
                  <br />
                  Currently, transiting {aspect.transitAspect.transit1.name} (in your {getOrdinal(aspect.transitAspect.transit1.house)} house) is forming a {aspect.transitAspect.aspect.name.toLowerCase()} to transiting {aspect.transitAspect.transit2.name} (in your {getOrdinal(aspect.transitAspect.transit2.house)} house).

                  <br /><br />
                  <strong>How Your Natal {aspect.natal.name} in the {getOrdinal(aspect.natal.house)} House Activates This Transit:</strong>
                  <br />
                  {manifestations.length > 0
                    ? manifestations.map(m => `• ${m}`).join('\n')
                    : `Your natal ${aspect.natal.name} from the {getOrdinal(aspect.natal.house)} house brings its energy to this transit configuration. The themes of your ${getOrdinal(aspect.natal.house)} house will be especially prominent in how you experience this ${aspect.transitAspect.transit1.name}-${aspect.transitAspect.transit2.name} transit.`}

                  <br /><br />
                  <strong>Working With This:</strong>
                  <br />
                  {aspect.aspect.name === 'Square' || aspect.aspect.name === 'Opposition'
                    ? `Use the friction to grow. The challenge is pushing you to evolve how you handle both your natal ${getOrdinal(aspect.natal.house)} house themes and the current transit circumstances.`
                    : `Take advantage of this supportive energy. Your natal strengths are perfectly positioned to help you navigate the current transits successfully.`}
                </div>

                <button
                  onClick={() => setExpandedAspectIndex(null)}
                  style={{
                    marginTop: '20px',
                    padding: '10px 20px',
                    backgroundColor: '#4a90e2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Close
                </button>
              </>
            );
          })()}
        </div>
      )}

      {/* Daily Briefing Panel */}
      {dailyBriefingIndex !== null && weeklyAspects[dailyBriefingIndex] && (() => {
        const dayData = weeklyAspects[dailyBriefingIndex];
        const activationsForSummary = dayData.aspects.map((a: any) => ({
          natalPlanet: a.natal.name,
          natalHouse: a.natal.house,
          transit1Planet: a.transitAspect.transit1.name,
          transit1House: a.transitAspect.transit1.house,
          transit2Planet: a.transitAspect.transit2.name,
          transit2House: a.transitAspect.transit2.house,
          natalAspectType: a.aspect.name,
          transitAspectType: a.transitAspect.aspect.name,
          orb: a.aspect.actualOrb
        }));
        const summary = generateDailySummary(activationsForSummary);

        const intensityColors: Record<string, { bg: string; border: string; text: string }> = {
          'Quiet': { bg: 'rgba(76,175,80,0.1)', border: '#66bb6a', text: '#4ade80' },
          'Active': { bg: 'rgba(66,165,245,0.1)', border: '#42a5f5', text: '#60a5fa' },
          'Intense': { bg: 'rgba(255,152,0,0.1)', border: '#ffa726', text: '#fbbf24' },
          'Pivotal': { bg: 'rgba(239,83,80,0.1)', border: '#ef5350', text: '#f87171' }
        };
        const iColors = intensityColors[summary.intensity];
        const intensityBars = Array(4).fill(0).map((_, i) => i < summary.intensityScore);

        return (
          <div style={{
            marginTop: '30px',
            padding: '25px',
            backgroundColor: '#12121f',
            borderRadius: '12px',
            border: `3px solid ${iColors.border}`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
              <div>
                <h3 style={{ margin: 0, color: '#c9a87c', fontSize: '20px' }}>
                  Daily Cosmic Briefing
                </h3>
                <div style={{ fontSize: '14px', color: '#8a8a9a', marginTop: '4px' }}>
                  {dayData.date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                </div>
              </div>
              <button
                onClick={() => setDailyBriefingIndex(null)}
                style={{
                  padding: '6px 16px',
                  backgroundColor: '#666',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                Close
              </button>
            </div>

            {/* Intensity Meter */}
            <div style={{
              padding: '12px 16px',
              backgroundColor: iColors.bg,
              borderRadius: '8px',
              border: `1px solid ${iColors.border}`,
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{ fontSize: '14px', fontWeight: 'bold', color: iColors.text, minWidth: '80px' }}>
                {summary.intensity}
              </div>
              <div style={{ display: 'flex', gap: '4px' }}>
                {intensityBars.map((filled, i) => (
                  <div key={i} style={{
                    width: '24px',
                    height: '10px',
                    borderRadius: '3px',
                    backgroundColor: filled ? iColors.border : 'rgba(255,255,255,0.1)',
                    transition: 'background-color 0.3s'
                  }} />
                ))}
              </div>
              <div style={{ fontSize: '13px', color: '#8a8a9a', flex: 1 }}>
                {summary.overview}
              </div>
            </div>

            {/* Life Event Alerts */}
            {summary.lifeEventAlerts.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                {summary.lifeEventAlerts.map((alert, i) => (
                  <div key={i} style={{
                    padding: '12px 16px',
                    backgroundColor: alert.type === 'money' ? 'rgba(255,193,7,0.1)' : alert.type === 'love' ? 'rgba(233,30,99,0.1)' : alert.type === 'career' ? 'rgba(63,81,181,0.1)' : alert.type === 'property' ? 'rgba(0,150,136,0.1)' : alert.type === 'travel' ? 'rgba(3,169,244,0.1)' : 'rgba(156,39,176,0.1)',
                    borderRadius: '8px',
                    border: `2px solid ${alert.type === 'money' ? '#ffc107' : alert.type === 'love' ? '#e91e63' : alert.type === 'career' ? '#3f51b5' : alert.type === 'property' ? '#009688' : alert.type === 'travel' ? '#03a9f4' : '#9c27b0'}`,
                    marginBottom: '10px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px'
                  }}>
                    <span style={{ fontSize: '22px', lineHeight: '1' }}>{alert.emoji}</span>
                    <div>
                      <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#ede8e0', marginBottom: '4px' }}>
                        {alert.label}
                      </div>
                      <div style={{ fontSize: '13px', color: '#8a8a9a', lineHeight: '1.5' }}>
                        {alert.message}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Focus Areas */}
            {summary.focusAreas.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#ede8e0' }}>
                  🎯 Today's Focus Areas
                </h4>
                {summary.focusAreas.map((area, areaIdx) => (
                  <div key={areaIdx} style={{
                    padding: '14px 16px',
                    backgroundColor: '#1a1a2e',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                    borderLeft: `4px solid ${area.hasHard && !area.hasSoft ? '#ff9800' : area.hasSoft && !area.hasHard ? '#4caf50' : '#5c6bc0'}`,
                    marginBottom: '10px'
                  }}>
                    <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#c9a87c', marginBottom: '8px' }}>
                      {area.domainLabel} ({area.house}{['th','st','nd','rd'][area.house % 100 > 3 && area.house % 100 < 21 ? 0 : area.house % 10 > 3 ? 0 : area.house % 10] || 'th'} house)
                    </div>
                    <div style={{ fontSize: '13px', color: '#8a8a9a', lineHeight: '1.7' }}>
                      {area.narrative}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Tensions */}
            {summary.tensions.length > 0 && (
              <div style={{
                padding: '14px 16px',
                backgroundColor: 'rgba(255,152,0,0.1)',
                borderRadius: '8px',
                border: '1px solid #ffcc80',
                marginBottom: '15px'
              }}>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '15px', color: '#fbbf24' }}>
                  ⚡ Watch Out For
                </h4>
                {summary.tensions.map((t, i) => (
                  <div key={i} style={{ fontSize: '13px', color: '#fbbf24', lineHeight: '1.6', marginBottom: '6px' }}>
                    • {t}
                  </div>
                ))}
              </div>
            )}

            {/* Opportunities */}
            {summary.opportunities.length > 0 && (
              <div style={{
                padding: '14px 16px',
                backgroundColor: 'rgba(76,175,80,0.1)',
                borderRadius: '8px',
                border: '1px solid #a5d6a7',
                marginBottom: '15px'
              }}>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '15px', color: '#4ade80' }}>
                  ✨ Lean Into
                </h4>
                {summary.opportunities.map((o, i) => (
                  <div key={i} style={{ fontSize: '13px', color: '#4ade80', lineHeight: '1.6', marginBottom: '6px' }}>
                    • {o}
                  </div>
                ))}
              </div>
            )}

            {/* Top Focus */}
            <div style={{
              padding: '14px 16px',
              backgroundColor: 'rgba(63,81,181,0.1)',
              borderRadius: '8px',
              border: '2px solid #5c6bc0',
              textAlign: 'center'
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '15px', color: '#818cf8' }}>
                🎯 Today's #1 Focus
              </h4>
              <div style={{ fontSize: '14px', color: '#c9a87c', lineHeight: '1.6', fontWeight: '500' }}>
                {summary.topFocus}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Color Legend — Collapsible (dark banner matching Display Options) */}
      <div style={{
        marginTop: '20px',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        padding: '14px 20px',
        borderRadius: '12px',
        border: '1px solid rgba(138, 43, 226, 0.3)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
      }}>
        <div
          onClick={() => setShowLegend(!showLegend)}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', userSelect: 'none' }}
        >
          <span style={{ fontSize: '15px', fontWeight: 600, color: '#fff' }}>Color Legend</span>
          <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', transition: 'transform 0.2s', transform: showLegend ? 'rotate(180deg)' : 'rotate(0deg)', display: 'inline-block' }}>▼</span>
        </div>
        {showLegend && (
          <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <div style={{ display: 'flex', gap: '40px', flexWrap: 'wrap' }}>
              <div style={{ flex: '1', minWidth: '280px' }}>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#ffd700', fontWeight: 600 }}>
                  Natal Planet Colors (Background)
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '6px' }}>
                  {Object.entries(PLANET_COLORS).map(([planet, color]) => (
                    <div key={planet} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'rgba(255,255,255,0.85)' }}>
                      <div style={{ width: '18px', height: '18px', backgroundColor: `${color}40`, border: '1px solid rgba(255,255,255,0.2)', borderRadius: '3px' }} />
                      <span>{PLANET_SYMBOLS[planet]} {planet}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ flex: '1', minWidth: '240px' }}>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#ffd700', fontWeight: 600 }}>
                  Aspect Colors (Border)
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '6px' }}>
                  {ASPECT_TYPES.map((aspect) => (
                    <div key={aspect.name} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'rgba(255,255,255,0.85)' }}>
                      <div style={{ width: '18px', height: '18px', backgroundColor: 'rgba(255,255,255,0.1)', border: `3px solid ${aspect.color}`, borderRadius: '3px' }} />
                      <span>{aspect.symbol} {aspect.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ marginTop: '12px', padding: '8px 12px', backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '5px', fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
              <strong style={{ color: '#ffd700' }}>How to read:</strong> The background color shows which natal planet is activated.
              The border color shows the type of aspect to the transit-to-transit midpoint.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
